import { message } from '@/components/Message';
import { NumericKeyboardInput } from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import { playSound } from '@/utils/sound';
import { InputNumber, Space } from 'antd';
import BigNumber from 'bignumber.js';
import React from 'react';

// 纯函数：根据当前金额计算下一次抹零后的目标金额
function getNextTrimmedValue(amount: BigNumber): BigNumber {
  // 1. 如果有小数，先抹去小数（向下取整）
  if (!amount.isInteger()) {
    return amount.integerValue(BigNumber.ROUND_FLOOR);
  }

  // 2. 整数抹零：从低位到高位，找到第一个非 0 数字置 0
  const digits = amount.toFixed(0);
  const highestDigit = digits[0];

  for (let i = digits.length - 1; i > 0; i--) {
    if (digits[i] !== '0') {
      const newDigits = digits.slice(0, i) + '0'.repeat(digits.length - i);
      const newVal = new BigNumber(newDigits);
      // 保证抹零后不会改变最高位数（如 12 变 10，但 10 变 0 属于非法）
      if (newVal.toFixed(0)[0] === highestDigit && newVal.gt(0)) {
        return newVal;
      }
    }
  }

  return amount; // 无法再抹
}

type Props = {
  beforeTotalPrice: BigNumber;
  value?: number | null;
  onChange?: (value: number | null) => void;
};

const WaiverAmountSelector: React.FC<Props> = ({ beforeTotalPrice, value, onChange }) => {
  const priceNum = beforeTotalPrice.toNumber();

  const handleReset = () => {
    onChange?.(null);
    playSound('click');
  };

  // 快速抹零处理逻辑
  const handleQuickWaiver = () => {
    playSound('click');
    // 当前剩余应付金额 = 原总价 - 当前优惠金额
    const currentDiscount = value || 0;
    const currentPayable = beforeTotalPrice.minus(currentDiscount);

    // 计算下一个安全抹零目标
    const targetPayable = getNextTrimmedValue(currentPayable);
    const waiverAmount = beforeTotalPrice.minus(targetPayable);

    if (waiverAmount.gt(currentDiscount)) {
      onChange?.(waiverAmount.toNumber());
    } else {
      message.info('已无法快速抹零', 1);
    }
  };

  return (
    <>
      {priceNum > 0 ? (
        <Space size={10}>
          <NumericKeyboardInput
            value={value ?? undefined}
            inputType={InputNumber}
            onChange={v => {
              if (v === null || v === undefined || v === '') {
                onChange?.(null);
              } else {
                const numVal = Number(v);
                if (numVal > priceNum) {
                  onChange?.(priceNum);
                } else {
                  onChange?.(numVal);
                }
              }
            }}
            inputProps={{
              placeholder: '减免金额',
              style: { width: '100%' },
              min: 0,
              max: priceNum,
              precision: 2,
            }}
            keyboardProps={{
              parseValueToNumber: true,
              precision: 2,
              showAllClean: true,
              placement: 'right',
              trigger: 'click',
              style: { width: 300 },
            }}
          />
          <PosButton onClick={handleQuickWaiver}>快速抹零</PosButton>
          <PosButton onClick={handleReset}>重置</PosButton>
        </Space>
      ) : (
        <div>无可减免金额</div>
      )}
    </>
  );
};

export default WaiverAmountSelector;
